import { HEADERS } from './fixtures';
import PropTypes from 'prop-types';
import { StatisticsLayout } from 'views/components';
import { config } from 'config';
import isEmpty from 'lodash/isEmpty';
import { statisticsActions } from 'core/Statistics';
import { useTranslation } from 'react-i18next';
import {
    BarChart,
    Card,
    InfoCard,
    PieChart
} from 'mol-ui';
import {
    Col,
    Row
} from 'reactstrap';
import React, { useMemo, useState } from 'react';
import {
    clearObject,
    objectToChart,
    parseGender
} from 'shared/utils';
import {
    useDispatch,
    useObjectState,
    useSelector,
    useWindowSize
} from 'hooks';
const { diagnosisLicense } = config;

const PatientsAttended  = ({
    tabController
}) => {

    const { t } = useTranslation();
    const { xs } = useWindowSize();
    const {
        loading,
        patientsAttended,
        patientsAttendedDownload,
        patientsAttendedDownloading
    } = useSelector('statistics');
    const dispatch = useDispatch();
    const [currentFilters, setCurrentFilters] = useState({});
    const [chartFilters, , setChartFilterValue] = useObjectState({
        diagnosis_id: null,
        gender: null,
        license_reason_id: null,
        position_id: null
    });

    const {
        diagnosis_id: diagnosisId,
        gender,
        license_reason_id: licenseReasonId,
        position_id: positionId
    } = chartFilters ?? {};

    const onFiltersChange = filters => {
        setCurrentFilters(filters);
        dispatch(statisticsActions.getStatisticsPatientsAttended(clearObject({
            ...filters,
            ...chartFilters
        })));
    };

    const {
        average_age,
        gender_distribution,
        total_attentions,
        top_diagnostics,
        top_license_reasons,
        top_positions
    } = patientsAttended;

    const genderConfig = {
        config: [{ dataKey: 'value', fontSize: xs ? '11px' : '12px', label: t('Distribución por género') }],
        data: objectToChart(gender_distribution, parseGender),
        disabled: loading,
        height: 250,
        selected: gender
    };
    
    const commonProps = {
        disabled: loading,
        labelMaxLength: 20,
        maxBarSize: 150,
        vertical: true,
        xAxisVerticalHeight: 100
    };

    const handleFilterChange = ({ key }, id) => {
        const currentValue = chartFilters[id];
        const nextValue = currentValue === key ? null : key;
        setChartFilterValue(id, nextValue);
        dispatch(statisticsActions.getStatisticsPatientsAttended(clearObject({
            ...currentFilters,
            ...chartFilters,
            [id]: nextValue
        })));
    };

    const downloadController = useMemo(() => ({
        data: patientsAttendedDownload,
        downloading: patientsAttendedDownloading,
        fileName: t('Pacientes atendidos'),
        headers: HEADERS,
        onDownload: filters => dispatch(statisticsActions.getStatisticsPatientsAttendedDownload({ ...filters, ...chartFilters }))
    }), [chartFilters, patientsAttendedDownload, patientsAttendedDownloading]);

    return (
        <StatisticsLayout
            downloadController={ downloadController }
            tabController={ tabController }
            onFiltersChange={ onFiltersChange }
        >
            <Row>
                <Col md="6" xs="12">
                    <InfoCard
                        caption={ t('Pacientes únicos atendidos') }
                        icon="summarize"
                        title={ (total_attentions ?? 0).toString() }
                    />
                </Col>
                <Col md="6" xs="12">
                    <InfoCard
                        caption={ t('Edad promedio') }
                        icon="insert_invitation"
                        title={ (average_age ?? 0).toString() }
                    />
                </Col>
                {
                    diagnosisLicense && !isEmpty(top_license_reasons) &&
                    <Col md="12" xs="12">
                        <Card title={ t('Por licencia') }>
                            <BarChart
                                { ...commonProps }
                                config={ [{ dataKey: 'amount', label: t('Top por licencia') }] }
                                data={ top_license_reasons }
                                selected={ licenseReasonId }
                                onClick={ value => handleFilterChange(value, 'license_reason_id') }
                            />
                        </Card>
                    </Col>
                }
                {
                    !isEmpty(top_diagnostics) &&
                    <Col md="12" xs="12">
                        <Card title={ t('Por diagnóstico') }>
                            <BarChart
                                { ...commonProps }
                                config={ [{ dataKey: 'amount', label: t('Top de diagnosticos') }] }
                                data={ top_diagnostics }
                                selected={ diagnosisId }
                                onClick={ value => handleFilterChange(value, 'diagnosis_id') }
                            />
                        </Card>
                    </Col>
                }
                {
                    !isEmpty(top_positions) &&
                    <Col md="12" xs="12">
                        <Card title={ t('Por cargo') }>
                            <BarChart
                                { ...commonProps }
                                config={ [{ dataKey: 'amount', label: t('Top por cargo') }] }
                                data={ top_positions }
                                selected={ positionId }
                                onClick={ value => handleFilterChange(value, 'position_id') }
                            />
                        </Card>
                    </Col>
                }
                {
                    !isEmpty(gender_distribution) &&
                    <Col xs="12">
                        <Card overflowX="auto" title={ t('Distribución por género') }>
                            <PieChart
                                { ...genderConfig }
                                onClick={ value => handleFilterChange(value, 'gender') }
                            />
                        </Card>
                    </Col>
                }
            </Row>
        </StatisticsLayout>
    );
};

PatientsAttended.propTypes = {
    tabController: PropTypes.object
};

export default PatientsAttended;
